import { List } from '@mui/material';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { DialogInfoTypography, DialogInfoTypographyBold, DialogInfoListItem } from './components';
import { useContract } from '../../../../hooks/requests/use-contract';
import { useReviseContract } from '../../../../hooks/requests/use-revise-contract/use-revise-contract';
import { useValidatedParams } from '../../../../hooks/use-validated-params';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import { parsePlanInput } from '../../../../utils/parse-plan-input';
import { runDebouncedMutation } from '../../../../utils/vy-mutation';
import type { NewPlanForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { userMessage } from '../../../../utils/vy-toasts';
import { isEqual } from 'lodash';
import { getCreatePlanDefaultValues } from '../../../plans/CreatePlanPage/create-plan-page-consts/create-plan-default-values';
import { useGetReviseCurrentContractPrerequisites } from '../contract-revise-page-hooks/use-get-revise-current-contract-prerequisites';
import { useCallback } from 'react';
import { formatDate } from '../../../../utils/date';
import { queryKeys } from '../../../../services/query-key/query-keys';
import {
  invalidateContractsQueries,
  invalidateCustomersQueries,
  invalidateBillingRevenueQueries,
} from '../../../../utils/query-invalidation';

type SaveReviseProps = {
  open: boolean;
  onClose?: () => void;
};

export const SaveReviseCurrentDialog: React.FC<SaveReviseProps> = ({ open, onClose }) => {
  const { contractId } = useValidatedParams('contractId');
  const queryClient = useQueryClient();
  const navigate = useVyNavigate();
  const { getValues } = useFormContext<PlanInputForm, NewPlanForm>();
  const reviseCurrentContractPrerequisites = useGetReviseCurrentContractPrerequisites();
  const newContractEndDate =
    reviseCurrentContractPrerequisites?.coTerm === false
      ? reviseCurrentContractPrerequisites?.newContractEndDate
      : undefined;

  const {
    data: { contract },
  } = useContract(contractId, {
    refetchOnWindowFocus: false,
  });

  const reviseContractMutation = useReviseContract();

  const handleSubmit = useCallback(async () => {
    const planInput = getValues();
    const parsedPlan = parsePlanInput(planInput, {
      errorContext: 'Revise current contract validation',
    });

    if (!parsedPlan.success) {
      return;
    }

    const originalPlanData = getCreatePlanDefaultValues({
      plan: contract.plan,
      startDate: contract.startDate,
      customerId: contract.customer.id,
    });
    const currentPlanData = getValues();
    const hasChanges = !isEqual(originalPlanData, currentPlanData);

    if (isNullOrUndefined(reviseCurrentContractPrerequisites)) {
      userMessage('error', 'Missing phase prerequisites data, either label or effective date.');
      return;
    }

    if (!hasChanges) {
      userMessage('error', 'No changes detected — revision not saved.');
      return;
    }

    await runDebouncedMutation(
      `revise-current-contract-${contractId}`,
      reviseContractMutation,
      {
        contractId,
        reviseContractRequest: {
          plan: parsedPlan.data,
          startDate: reviseCurrentContractPrerequisites.effectiveDate,
          newContractEndDate: reviseCurrentContractPrerequisites.coTerm
            ? undefined
            : reviseCurrentContractPrerequisites.newContractEndDate,
          phaseMetadata: {
            name: reviseCurrentContractPrerequisites.name,
            label: reviseCurrentContractPrerequisites.label,
          },
        },
      },
      {
        pending: 'Updating...',
        success: 'Updated',
        error: 'Failed to revise contract',
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              predicate: (query) => query.queryKey.includes(contractId),
            }),
            invalidateContractsQueries(queryClient),
            invalidateCustomersQueries(queryClient),
            invalidateBillingRevenueQueries(queryClient),
            queryClient.invalidateQueries(queryKeys.creditPools()),
          ]);

          const queryKey = queryKeys.contractPage(contractId);

          await queryClient.invalidateQueries(queryKey);
          await queryClient.refetchQueries(queryKey);

          onClose?.();
          navigate(`/plans/contracts/${contractId}`);
        },
      },
    );
  }, []);

  return (
    <VyDialog
      maxWidth="sm"
      label="Your updated contract is ready to go"
      labelDescription="Save the changes and modifications you applied to the contract"
      open={open}
      onClose={onClose}
      ctaText="Update Contract"
      onSubmit={handleSubmit}
      cancelButtonText="Cancel"
    >
      <List sx={{ listStyleType: 'disc', px: 3 }}>
        <DialogInfoListItem>
          {reviseCurrentContractPrerequisites && (
            <DialogInfoTypography>
              Changes will take effect on
              <DialogInfoTypographyBold sx={{ display: 'inline' }}>
                {` ${formatDate(reviseCurrentContractPrerequisites.effectiveDate)}`}
              </DialogInfoTypographyBold>
            </DialogInfoTypography>
          )}
        </DialogInfoListItem>
        <DialogInfoListItem>
          <DialogInfoTypography>
            The contract start date, billing interval, and duration
            <DialogInfoTypographyBold sx={{ display: 'inline' }}> will remain the same.</DialogInfoTypographyBold>
          </DialogInfoTypography>
        </DialogInfoListItem>
        {isNotNullOrUndefined(newContractEndDate) && (
          <DialogInfoListItem>
            <DialogInfoTypography>
              The contract end date will be updated to
              <DialogInfoTypographyBold sx={{ display: 'inline' }}>
                {` ${formatDate(newContractEndDate)}`}
              </DialogInfoTypographyBold>
            </DialogInfoTypography>
          </DialogInfoListItem>
        )}
        <DialogInfoListItem>
          <DialogInfoTypography>
            Approved invoices will not be changed, and cannot be modified. The system may generate a new
            invoice if needed.
          </DialogInfoTypography>
        </DialogInfoListItem>
        <DialogInfoListItem>
          <DialogInfoTypography>
            Draft and in-review invoices may be
            <DialogInfoTypographyBold sx={{ display: 'inline' }}> regenerated</DialogInfoTypographyBold> based on the
            defined effective date.
          </DialogInfoTypography>
        </DialogInfoListItem>
      </List>
    </VyDialog>
  );
};
